import { useCallback, useMemo } from 'react';
import { Controller, UseFormReturn, useWatch } from 'react-hook-form';
import { Box, Button, InputAdornment, Paper, Stack, TextField, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { TProjectFormData } from 'src/@types';
import ExcelFormButtons from 'src/components/ExcelFormButtons';
import { RHFTextField } from 'src/components/hook-form';
import Iconify from 'src/components/iconify';
import ItemActionMenu from 'src/components/ItemActionMenu';
import { useExcelFormField } from 'src/hooks/useExcelFormField';
import { numberToSimpleKoreanCurrency } from 'src/utils/koreanCurrency';
import {
  downloadPurchaseAssignmentTemplate,
  parsePurchaseAssignmentExcel,
  PurchaseAssignmentRow,
} from 'src/utils/projectExcelUtils';

export interface PurchaseAssignmentSectionProps {
  methods: UseFormReturn<TProjectFormData>;
  readonly?: boolean;
}

type CategoryGroup = {
  category: string;
  indices: number[];
};

type FormRow = NonNullable<TProjectFormData['purchaseAssignments']>[number];

/**
 * 과제용 매입내역 섹션 컴포넌트
 * - 비목별 그룹핑 및 합산액/잔액 표시
 * - 비목, 계획, 협약금액, 사용금액, 잔액, 비고
 */
function PurchaseAssignmentSection({ methods, readonly }: PurchaseAssignmentSectionProps) {
  const { enqueueSnackbar } = useSnackbar();

  const {
    fields: purchaseAssignments,
    append: appendPurchaseAssignment,
    remove: removePurchaseAssignment,
    insert: insertPurchaseAssignment,
    fileInputRef,
    handleDownload,
    handleUpload,
    handleClickUpload,
  } = useExcelFormField<TProjectFormData, 'purchaseAssignments', PurchaseAssignmentRow, FormRow>({
    methods,
    fieldName: 'purchaseAssignments',
    downloadFn: downloadPurchaseAssignmentTemplate,
    parseFn: parsePurchaseAssignmentExcel,
    formToExcel: (item) => ({
      category: item.category || '',
      plan: item.plan || '',
      contractAmt: item.contractAmt || 0,
      usedAmt: item.usedAmt || 0,
      memo: item.memo || null,
    }),
    excelToForm: (row) => ({
      category: row.category,
      plan: row.plan,
      contractAmt: row.contractAmt,
      usedAmt: row.usedAmt,
      memo: row.memo || '',
    }),
    labels: {
      download: '매입내역_과제',
      upload: '건의 매입내역이 업로드되었습니다.',
      empty: '업로드할 데이터가 없습니다.',
    },
  });

  // 실시간 값 감시 (금액 계산용)
  const watchedValues = useWatch({
    control: methods.control,
    name: 'purchaseAssignments',
  });

  // 비목별 그룹핑 (fields 기반으로 동기화 보장)
  const categoryGroups = useMemo(() => {
    const groupMap = new Map<string, CategoryGroup>();

    purchaseAssignments.forEach((field, index) => {
      // watchedValues에서 실시간 category 값 가져오기
      const category = watchedValues?.[index]?.category ?? '';

      // 빈 비목은 개별 그룹으로 처리 (각각의 validation 에러 표시를 위해)
      if (!category) {
        const uniqueKey = `empty-${field.id}`;
        groupMap.set(uniqueKey, {
          category: '',
          indices: [index],
        });
      } else if (groupMap.has(category)) {
        groupMap.get(category)?.indices.push(index);
      } else {
        groupMap.set(category, {
          category,
          indices: [index],
        });
      }
    });

    return Array.from(groupMap.values());
  }, [purchaseAssignments, watchedValues]);

  // 매입내역 복사 함수
  const handleCopyItem = useCallback(
    (index: number) => {
      const currentValues = methods.getValues('purchaseAssignments');
      const itemToCopy = currentValues?.[index];

      if (itemToCopy) {
        const copiedItem = {
          category: itemToCopy.category || '',
          plan: itemToCopy.plan || '',
          contractAmt: itemToCopy.contractAmt || 0,
          usedAmt: itemToCopy.usedAmt || 0,
          memo: itemToCopy.memo || '',
        };

        insertPurchaseAssignment(index + 1, copiedItem);
        enqueueSnackbar('계획이 복사되었습니다.', { variant: 'success' });
      }
    },
    [insertPurchaseAssignment, methods, enqueueSnackbar],
  );

  // 비목 내 계획 추가
  const handleAddPlan = useCallback(
    (category: string) => {
      appendPurchaseAssignment({
        category,
        plan: '',
        contractAmt: 0,
        usedAmt: 0,
        memo: '',
      });
    },
    [appendPurchaseAssignment],
  );

  // 새 비목 추가
  const handleAddCategory = useCallback(() => {
    // 기존 비목명들 확인하여 고유한 이름 생성
    const currentValues = methods.getValues('purchaseAssignments') || [];
    const existingCategories = new Set(currentValues.map((item) => item.category));

    let newCategoryName = '새 비목';
    let counter = 1;
    while (existingCategories.has(newCategoryName)) {
      newCategoryName = `새 비목 ${counter}`;
      counter++;
    }

    appendPurchaseAssignment({
      category: newCategoryName,
      plan: '',
      contractAmt: 0,
      usedAmt: 0,
      memo: '',
    });
  }, [appendPurchaseAssignment, methods]);

  // 비목 전체 삭제 (그룹 내 모든 항목 삭제)
  const handleDeleteCategory = useCallback(
    (fieldIds: string[]) => {
      // id를 기반으로 현재 인덱스를 찾아서 내림차순으로 삭제
      const currentFields = purchaseAssignments;
      const indicesToRemove = fieldIds
        .map((id) => currentFields.findIndex((field) => field.id === id))
        .filter((index) => index !== -1)
        .sort((a, b) => b - a);

      indicesToRemove.forEach((index) => {
        removePurchaseAssignment(index);
      });
      enqueueSnackbar('비목이 삭제되었습니다.', { variant: 'success' });
    },
    [purchaseAssignments, removePurchaseAssignment, enqueueSnackbar],
  );

  // 개별 항목의 잔액 계산
  const getRemaining = (index: number) => {
    const values = watchedValues || [];
    const item = values[index];
    if (!item) return 0;
    return (Number(item.contractAmt) || 0) - (Number(item.usedAmt) || 0);
  };

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {!readonly && (
        <ExcelFormButtons
          fileInputRef={fileInputRef}
          onDownload={handleDownload}
          onClickUpload={handleClickUpload}
          onUpload={handleUpload}
          onAdd={handleAddCategory}
          addLabel="비목 추가"
        />
      )}

      {purchaseAssignments.length === 0 ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{
            p: 3,
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1,
            color: 'text.secondary',
          }}
        >
          <Typography variant="body2">매입내역이 없습니다.</Typography>
        </Box>
      ) : (
        // 비목별 그룹 렌더링
        categoryGroups.map((group) => {
          // 그룹의 첫 번째 인덱스를 사용하여 비목명 제어
          const firstIndex = group.indices[0];
          // 안정적인 key를 위해 첫 번째 항목의 id 사용 (category가 변경되어도 focus 유지)
          const groupKey = purchaseAssignments[firstIndex]?.id || `group-${firstIndex}`;

          return (
            <Paper
              key={groupKey}
              variant="outlined"
              sx={{
                overflow: 'hidden',
              }}
            >
              {/* 비목 헤더 */}
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  bgcolor: 'grey.100',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  justifyContent="space-between"
                  flex={1}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Controller
                      name={`purchaseAssignments.${firstIndex}.category`}
                      control={methods.control}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          label="비목"
                          size="small"
                          error={!!error}
                          helperText={error?.message}
                          inputProps={{ readOnly: readonly, maxLength: 100 }}
                          onChange={(e) => {
                            const newValue = e.target.value;
                            const oldValue = field.value;
                            // 현재 필드 업데이트
                            field.onChange(newValue);
                            // 해당 그룹의 다른 항목들도 비목 업데이트
                            if (group.indices.length > 1) {
                              group.indices.forEach((idx) => {
                                if (idx !== firstIndex) {
                                  const currentCategory = methods.getValues(
                                    `purchaseAssignments.${idx}.category`,
                                  );
                                  if (currentCategory === oldValue) {
                                    methods.setValue(
                                      `purchaseAssignments.${idx}.category`,
                                      newValue,
                                    );
                                  }
                                }
                              });
                            }
                          }}
                          sx={{ minWidth: 200 }}
                        />
                      )}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {group.indices.length}건
                    </Typography>
                  </Stack>
                  {!readonly && (
                    <Button
                      type="button"
                      size="small"
                      color="error"
                      variant="contained"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                      onClick={() =>
                        handleDeleteCategory(
                          group.indices.map((idx) => purchaseAssignments[idx]?.id).filter(Boolean),
                        )
                      }
                    >
                      <Iconify icon="eva:trash-2-fill" width={14} />
                      비목 삭제
                    </Button>
                  )}
                </Stack>
              </Box>

              {/* 비목 내 계획 항목들 */}
              <Box sx={{ p: 2 }}>
                <Stack spacing={2}>
                  {group.indices.map((index) => {
                    const remaining = getRemaining(index);
                    const itemKey = purchaseAssignments[index]?.id || `item-${index}`;
                    return (
                      <Box
                        key={itemKey}
                        sx={{
                          p: 2,
                          pl: readonly ? 2 : 4,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          bgcolor: 'background.paper',
                          position: 'relative',
                        }}
                      >
                        {/* 계획 + 협약금액 + 사용금액 + 잔액 */}
                        <Box
                          display="flex"
                          flexDirection="row"
                          flexWrap="wrap"
                          gap={2}
                          alignItems="flex-start"
                        >
                          <RHFTextField
                            name={`purchaseAssignments.${index}.plan`}
                            label="계획"
                            inputProps={{ readOnly: readonly, maxLength: 100 }}
                            size="small"
                            sx={{ flex: 2, minWidth: 200 }}
                          />
                          <Controller
                            name={`purchaseAssignments.${index}.contractAmt`}
                            control={methods.control}
                            render={({ field: { ref, ...field }, fieldState: { error } }) => (
                              <TextField
                                {...field}
                                label="협약금액"
                                value={field.value ? field.value.toLocaleString() : ''}
                                placeholder="협약금액"
                                error={!!error}
                                size="small"
                                onChange={(e) => {
                                  const value = e.target.value;
                                  const numericValue = Number(value.replace(/,/g, ''));
                                  if (!isNaN(numericValue)) {
                                    field.onChange(numericValue);
                                  }
                                }}
                                InputProps={{
                                  endAdornment: <InputAdornment position="end">원</InputAdornment>,
                                  readOnly: readonly,
                                }}
                                helperText={
                                  error?.message ||
                                  (field.value ? numberToSimpleKoreanCurrency(field.value) : '')
                                }
                                inputRef={ref}
                                sx={{ flex: 1, minWidth: 150 }}
                              />
                            )}
                          />
                          <Controller
                            name={`purchaseAssignments.${index}.usedAmt`}
                            control={methods.control}
                            render={({ field: { ref, ...field }, fieldState: { error } }) => (
                              <TextField
                                {...field}
                                label="사용금액"
                                value={field.value ? field.value.toLocaleString() : ''}
                                placeholder="사용금액"
                                error={!!error}
                                size="small"
                                onChange={(e) => {
                                  const value = e.target.value;
                                  const numericValue = Number(value.replace(/,/g, ''));
                                  if (!isNaN(numericValue)) {
                                    field.onChange(numericValue);
                                  }
                                }}
                                InputProps={{
                                  endAdornment: <InputAdornment position="end">원</InputAdornment>,
                                  readOnly: readonly,
                                }}
                                helperText={
                                  error?.message ||
                                  (field.value ? numberToSimpleKoreanCurrency(field.value) : '')
                                }
                                inputRef={ref}
                                sx={{ flex: 1, minWidth: 150 }}
                              />
                            )}
                          />
                          {/* 잔액 표시 (읽기 전용) */}
                          <TextField
                            label="잔액"
                            value={remaining.toLocaleString()}
                            size="small"
                            InputProps={{
                              endAdornment: <InputAdornment position="end">원</InputAdornment>,
                              readOnly: true,
                            }}
                            helperText={remaining ? numberToSimpleKoreanCurrency(remaining) : ''}
                            sx={{
                              flex: 1,
                              '& .MuiInputBase-input': {
                                color: remaining >= 0 ? 'success.main' : 'error.main',
                                fontWeight: 'bold',
                              },
                              minWidth: 150,
                            }}
                          />
                          <RHFTextField
                            name={`purchaseAssignments.${index}.memo`}
                            label="비고"
                            inputProps={{ readOnly: readonly }}
                            size="small"
                            multiline
                            sx={{ flex: 2, minWidth: 200 }}
                          />
                        </Box>
                        {!readonly && (
                          <ItemActionMenu
                            onDelete={() => removePurchaseAssignment(index)}
                            onCopy={() => handleCopyItem(index)}
                            deleteLabel="삭제"
                            copyLabel="복사"
                          />
                        )}
                      </Box>
                    );
                  })}
                </Stack>

                {/* 계획 추가 버튼 */}
                {!readonly && (
                  <Button
                    type="button"
                    variant="text"
                    size="small"
                    startIcon={<Iconify icon="eva:plus-fill" />}
                    onClick={() => handleAddPlan(group.category)}
                    sx={{ mt: 1 }}
                  >
                    계획 추가
                  </Button>
                )}
              </Box>
            </Paper>
          );
        })
      )}
    </Box>
  );
}

export default PurchaseAssignmentSection;
